import {
  PORTION_UNITS,
  type AdminFoodRequestDetail,
  type AdminFoodRequestField,
  type FoodMacrosView,
  type FoodResearchFieldName,
  type PortionUnitName,
} from '@steado/admin-contract/foods';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CheckIcon, ChevronDownIcon, MinusIcon, PencilIcon, TriangleAlertIcon } from 'lucide-react';
import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/lib/auth/auth-context';
import { cn } from '@/lib/utils';
import { invalidateCatalog } from '@/screens/catalog-history/catalog-history-query';
import { Figures, StatusBanner } from '@/screens/catalog-history/history-parts';

import { macroGap, macroKcal } from './fix-model';
import { FoodPicker } from './FoodPicker';
import type { ReviewProps } from './FixReview';
import {
  createFood,
  invalidateRequests,
  keepEstimate,
  rejectFoodRequest,
  researchAgain,
  resolveFoodRequest,
} from './food-requests-query';
import {
  EMPTY_NUTRITION,
  chosenMacros,
  isDecided,
  kcalOptions,
  newFoodDecisions,
  newFoodFlags,
  ownMacros,
  ownPortion,
  type FlagChoice,
  type FlagChoices,
  type NewFoodFlag,
  type OptionId,
} from './new-food-model';
import {
  CONFIDENCE_LABELS,
  ORIGIN_LABELS,
  SOURCE_NAMES,
  ago,
  confidenceOf,
  figure,
  plural,
  type ConfidenceLevel,
} from './request-copy';
import {
  ADMIN_ONLY,
  Card,
  ConfidenceBadge,
  ConfidenceDot,
  KeyHints,
  KindBadge,
  RejectForm,
  ResearchForm,
  SourceItem,
  SourcesPanel,
} from './review-parts';

const SOURCE_PREFIX = 'new-source';

const MEAL_TYPES: Readonly<Record<string, string>> = {
  breakfast: 'breakfast',
  mid_morning: 'mid-morning',
  lunch: 'lunch',
  snack: 'snack',
  dinner: 'dinner',
};

/**
 * A new food, reviewed from its research: the fields the AI was unsure of
 * (or could not find) decided first, nothing chosen for the reviewer; the
 * rest of the draft and the sources behind it; the catalog foods it may
 * duplicate, to point at instead; and the meals already logged on its AI
 * estimate, now and after. Create waits, and says why, until every flagged
 * field has a value.
 */
export function NewFoodReview({ request, position, onSkip, onAnswered }: ReviewProps) {
  const { api, can } = useAuth();
  const queryClient = useQueryClient();
  const draft = request.draft;
  const flags = useMemo(() => newFoodFlags(request), [request]);
  const [choices, setChoices] = useState<FlagChoices>({});
  const [openSources, setOpenSources] = useState<ReadonlySet<FoodResearchFieldName>>(new Set());
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState('name');
  const [queued, setQueued] = useState(false);
  const whyId = useId();
  const name = draft?.food?.name ?? request.name;
  const estimate = draft?.current ?? null;
  /** The AI estimate it was logged as, while it is one: what "Keep the estimate" keeps. */
  const estimateMacros = request.food?.source === 'ai_estimated' ? (estimate?.macrosPer100g ?? null) : null;

  const refresh = () => {
    invalidateRequests(queryClient);
    invalidateCatalog(queryClient);
  };

  const create = useMutation({
    mutationFn: () => createFood(api, request.id, { decisions: newFoodDecisions(request, flags, choices) }),
    onSuccess: () => {
      refresh();
      onAnswered();
    },
    retry: false,
  });
  const point = useMutation({
    mutationFn: (foodId: string) => resolveFoodRequest(api, request.id, { foodId }),
    onSuccess: () => {
      refresh();
      onAnswered();
    },
    retry: false,
  });
  const keep = useMutation({
    mutationFn: (reason: string) => keepEstimate(api, request.id, reason),
    onSuccess: () => {
      refresh();
      onAnswered();
    },
    retry: false,
  });
  const reject = useMutation({
    mutationFn: (reason: string) => rejectFoodRequest(api, request.id, reason),
    onSuccess: () => {
      refresh();
      onAnswered();
    },
    retry: false,
  });
  const research = useMutation({
    mutationFn: (note: string) => researchAgain(api, request.id, note),
    onSuccess: () => {
      setQueued(true);
      refresh();
    },
    retry: false,
  });

  const decided = flags.filter((flag) => isDecided(flag, choices[flag.field]));
  const left = flags.length - decided.length;
  const noDraft = draft === null || request.draftStatus !== 'ready';
  const why = !can.mutate
    ? ADMIN_ONLY
    : noDraft
      ? 'The research has not drafted this food yet.'
      : left > 0
        ? `Check ${plural(left, 'field')} first: ${flags
            .filter((flag) => !isDecided(flag, choices[flag.field]))
            .map((flag) => flag.label.toLowerCase())
            .join(', ')}.`
        : null;

  const macros = chosenMacros(request, flags, choices);
  const range = kcalOptions(request);
  const portion = chosenPortionGrams(request, flags, choices);

  const pick = (field: FoodResearchFieldName, choice: FlagChoice) => {
    setChoices((current) => ({ ...current, [field]: choice }));
  };

  // 1, 2 or 3 picks an option of the first field left; Enter creates once every one is picked.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, [role="dialog"]') != null) return;
      const first = flags.find((flag) => !isDecided(flag, choices[flag.field]));
      const index = Number(event.key) - 1;
      if (first !== undefined && Number.isInteger(index) && index >= 0 && index < first.options.length) {
        const option = first.options[index];
        if (option !== undefined) {
          event.preventDefault();
          pick(first.field, { option: option.id, ...defaultsFor(first.field, option.id, request) });
        }
      } else if (event.key === 'Enter' && target?.closest('button, a') == null && why === null) {
        event.preventDefault();
        create.mutate();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [flags, choices, why, create, request]);

  return (
    <div className="mx-auto flex w-full max-w-[1300px] flex-col gap-3.5 pb-2">
      <p className="text-xs text-muted-foreground">
        New food request{position === null ? '' : ` · ${position}`}
      </p>

      <section
        aria-labelledby="food-title"
        className="overflow-hidden rounded-xl border border-border bg-card"
      >
        <div className="flex flex-wrap items-start gap-3.5 px-[18px] pt-4 pb-3.5">
          <div className="flex min-w-0 flex-[1_1_360px] flex-col gap-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <h1 id="food-title" className="text-[17px] font-semibold tracking-[-0.01em] text-foreground">
                {name}
              </h1>
              <KindBadge kind="new_food" />
              <ConfidenceBadge level={confidenceOf(request)} />
            </div>
            <p className="text-xs text-muted-foreground">
              {ORIGIN_LABELS[request.origin]} · {plural(request.userCount, 'person', 'people')}
              {request.estimateLogs === null
                ? ''
                : ` · ${plural(request.estimateLogs.count, 'log')} on an AI estimate`}
              {request.researchedAt === null
                ? ' · not researched yet'
                : ` · researched ${ago(request.researchedAt)} from ${plural(request.sources.length, 'source')}`}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              className="border-err-fg/30 text-err-fg hover:bg-err-bg hover:text-err-fg"
              asChild
            >
              <a href="#decide">Reject</a>
            </Button>
            <Button variant="outline" asChild>
              <a href="#dupes">Point at a food</a>
            </Button>
          </div>
        </div>
      </section>

      {queued || request.draftStatus === 'queued' ? (
        <StatusBanner tone="mute">
          Researching {name} again. It comes back to the queue when its sources are in.
        </StatusBanner>
      ) : null}

      {flags.length === 0 || noDraft ? null : (
        <section
          aria-labelledby="flags-title"
          className="overflow-hidden rounded-xl border border-warn-fg/25 bg-card"
        >
          <header className="flex flex-wrap items-center gap-x-2 gap-y-1.5 bg-warn-bg px-[18px] py-3">
            <TriangleAlertIcon aria-hidden="true" className="size-[15px] shrink-0 text-warn-fg" />
            <h2 id="flags-title" className="text-[15px] font-semibold text-foreground">
              Check these first
            </h2>
            <span className="text-xs text-muted-foreground">
              The AI was unsure about these. Pick a value for each; nothing is chosen for you.
            </span>
            <span role="status" className="ml-auto text-xs font-medium text-warn-fg tabular-nums">
              {decided.length} of {flags.length} checked
            </span>
          </header>
          {flags.map((flag) => (
            <FlagBlock
              key={flag.field}
              flag={flag}
              request={request}
              choice={choices[flag.field]}
              portionGrams={portion}
              macros={macros}
              sourcesOpen={openSources.has(flag.field)}
              onToggleSources={() => {
                setOpenSources((current) => {
                  const next = new Set(current);
                  if (next.has(flag.field)) next.delete(flag.field);
                  else next.add(flag.field);
                  return next;
                });
              }}
              onPick={(choice) => {
                pick(flag.field, choice);
              }}
            />
          ))}
        </section>
      )}

      {noDraft ? (
        <Card title="Not drafted yet">
          <p className="border-t border-border px-[18px] py-3.5 text-[13px] text-muted-foreground">
            {request.draftStatus === 'failed'
              ? `The research found nothing to draft from${request.researchError === null ? '' : `: ${request.researchError}`}. Research it again with a note, point it at a food, or reject it below.`
              : 'The research is running. This page shows its draft when its sources are in.'}
          </p>
        </Card>
      ) : (
        <div className="flex flex-wrap items-start gap-3.5">
          <DraftPanel
            request={request}
            flags={flags}
            choices={choices}
            showAll={showAll}
            selected={selected}
            onToggle={() => {
              setShowAll((open) => !open);
            }}
            onSelect={setSelected}
          />
          <div className="flex min-w-0 flex-[10_1_400px] flex-col gap-3.5">
            {showAll ? (
              <EvidencePanel request={request} flags={flags} choices={choices} selected={selected} />
            ) : null}
            <SourcesPanel
              title="All sources the AI read"
              sources={request.sources}
              idPrefix={SOURCE_PREFIX}
            />
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-start gap-3.5">
        <Card id="dupes" title="Is it already in the catalog?" className="flex-[1_1_460px]">
          <p className="-mt-2 px-[18px] pb-3 text-xs text-muted-foreground">
            Closest foods by name, per 100 g. Pointing the request at one moves the estimate’s meals there.
          </p>
          <div className="grid grid-cols-[minmax(0,1.4fr)_64px_72px_112px] gap-3 bg-secondary px-[18px] py-2 text-[11px] font-semibold tracking-[0.055em] text-muted-foreground uppercase">
            <span>Food</span>
            <span>kcal</span>
            <span>Fat</span>
            <span />
          </div>
          {request.candidates.length === 0 ? (
            <p className="border-t border-border px-[18px] py-2.5 text-[13px] text-muted-foreground">
              No catalog food has a name like it.
            </p>
          ) : null}
          {request.candidates.map((candidate) => (
            <div
              key={candidate.id}
              className="grid grid-cols-[minmax(0,1.4fr)_64px_72px_112px] items-center gap-3 border-t border-border px-[18px] py-2"
            >
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="font-medium text-foreground">{candidate.name}</span>
                <span className="text-xs text-muted-foreground">
                  {SOURCE_NAMES[candidate.source]}
                  {candidate.isVerified ? ', verified' : ''}
                  {candidate.defaultPortion === null
                    ? ''
                    : ` · 1 ${candidate.defaultPortion.label} ${figure(candidate.defaultPortion.grams)} g`}
                </span>
              </span>
              <span className="font-mono text-[13px] tabular-nums">
                {figure(candidate.macrosPer100g.kcal)}
              </span>
              <span className="text-[13px]">
                <span className="font-mono tabular-nums">{candidate.macrosPer100g.fatG.toFixed(1)}</span> g
              </span>
              <span className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  aria-label={`Point at ${candidate.name}`}
                  aria-disabled={!can.mutate || point.isPending}
                  className={cn((!can.mutate || point.isPending) && 'cursor-not-allowed opacity-60')}
                  title={can.mutate ? undefined : ADMIN_ONLY}
                  onClick={() => {
                    if (can.mutate && !point.isPending) point.mutate(candidate.id);
                  }}
                >
                  {point.isPending && point.variables === candidate.id ? 'Pointing…' : 'Point here'}
                </Button>
              </span>
            </div>
          ))}
          {estimateMacros !== null ? (
            <div className="grid grid-cols-[minmax(0,1.4fr)_64px_72px_112px] items-center gap-3 border-t border-border bg-secondary/50 px-[18px] py-2">
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="font-medium text-foreground">
                  {request.food?.name ?? name}, the estimate
                </span>
                <span className="text-xs text-muted-foreground">
                  AI estimate, unreviewed · merges into the new food
                </span>
              </span>
              <span className="font-mono text-[13px] text-muted-foreground tabular-nums">
                {figure(estimateMacros.kcal)}
              </span>
              <span className="text-[13px] text-muted-foreground">
                <span className="font-mono tabular-nums">{estimateMacros.fatG.toFixed(1)}</span> g
              </span>
              <span className="flex justify-end text-xs text-muted-foreground">This request</span>
            </div>
          ) : null}
          <div className="border-t border-border px-[18px] pt-2.5 pb-3.5">
            <FoodPicker
              label="Or search the catalog"
              placeholder="Pound cake, fruit cake, tea cake"
              exclude={request.foodId}
              disabled={!can.mutate}
              disabledWhy={ADMIN_ONLY}
              pendingId={point.isPending ? point.variables : null}
              onPick={(food) => {
                point.mutate(food.id);
              }}
            />
            {point.isError ? (
              <p role="alert" className="mt-2 text-[12.5px] font-medium text-err-fg">
                {point.error.message}
              </p>
            ) : null}
          </div>
        </Card>

        <LogsPanel request={request} macros={macros} range={range} />
      </div>

      <Card id="decide" title="Reject or research again">
        <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] border-t border-border [&>*:last-child]:border-r-0">
          <div className="flex flex-col gap-2 border-r border-border px-[18px] pt-3.5 pb-4">
            <h3 className="text-[13px] font-semibold text-foreground">Research again</h3>
            <ResearchForm
              canMutate={can.mutate}
              isPending={research.isPending}
              error={research.isError ? research.error.message : null}
              placeholder={`Look for ${name} as made and sold in India.`}
              onResearch={(note) => {
                research.mutate(note);
              }}
            />
          </div>
          {estimateMacros !== null ? (
            <KeepEstimate
              kcal={estimateMacros.kcal}
              researched={draft?.proposed?.macrosPer100g?.kcal ?? null}
              canMutate={can.mutate}
              isPending={keep.isPending}
              error={keep.isError ? keep.error.message : null}
              onKeep={(reason) => {
                keep.mutate(reason);
              }}
            />
          ) : null}
          <div className="flex flex-col gap-2 px-[18px] pt-3.5 pb-4">
            <h3 className="text-[13px] font-semibold text-foreground">Reject</h3>
            <RejectForm
              canMutate={can.mutate}
              isPending={reject.isPending}
              error={reject.isError ? reject.error.message : null}
              onReject={(reason) => {
                reject.mutate(reason);
              }}
            />
          </div>
        </div>
      </Card>

      <div className="sticky bottom-3 z-[2] flex flex-wrap items-center gap-x-4 gap-y-2.5 rounded-xl border border-input bg-card px-[18px] py-3.5 shadow-[0_6px_20px_rgb(19_19_21/10%)]">
        <span className="flex min-w-0 flex-[1_1_360px] flex-col gap-1">
          <span className={cn('font-medium', why === null ? 'text-ok-fg' : 'text-warn-fg')}>
            {noDraft
              ? 'Not drafted yet'
              : left === 0
                ? flags.length === 0
                  ? 'Nothing to check'
                  : `All ${plural(flags.length, 'field')} checked`
                : `${plural(left, 'field')} left to check`}
          </span>
          <span className="max-w-[70ch] text-xs text-muted-foreground">
            Creates the food from this draft and your choices, resolves the request and merges the estimate
            into it, reviewed by you. It goes into History with its sources, where it can be reverted.
          </span>
          <KeyHints
            label="Keyboard: 1, 2 or 3 picks an option, Enter creates"
            keys={[
              ['1', ''],
              ['2', ''],
              ['3', 'pick'],
              ['Enter', 'create'],
            ]}
          />
          {create.isError ? (
            <span role="alert" className="text-[12.5px] font-medium text-err-fg">
              {create.error.message}
            </span>
          ) : null}
        </span>
        <Button variant="ghost" size="lg" className="text-muted-foreground" onClick={onSkip}>
          Skip for now
        </Button>
        <Button
          size="lg"
          aria-disabled={why !== null || create.isPending}
          aria-describedby={why === null ? undefined : whyId}
          className={cn(
            'font-semibold',
            why !== null &&
              'cursor-not-allowed border border-border bg-muted text-muted-foreground shadow-none hover:bg-muted',
          )}
          onClick={() => {
            if (why === null && !create.isPending) create.mutate();
          }}
        >
          {create.isPending
            ? 'Creating…'
            : left > 0 && !noDraft
              ? `Check ${plural(left, 'field')} to create`
              : `Create ${name}`}
        </Button>
        {why === null ? null : (
          <span id={whyId} className="sr-only">
            {why}
          </span>
        )}
      </div>
    </div>
  );
}

/** What a picked option starts with: your own fields prefilled from what is on the page. */
function defaultsFor(field: FoodResearchFieldName, option: OptionId, request: AdminFoodRequestDetail) {
  if (option !== 'own') return {};
  if (field === 'nutrition') return { nutrition: EMPTY_NUTRITION };
  const unit: PortionUnitName = request.draft?.food?.portions[0]?.unit ?? 'piece';
  return { portion: { label: request.draft?.food?.portions[0]?.label ?? 'slice', unit, grams: '' } };
}

/** Grams of the portion the new food would default to, as chosen so far. */
function chosenPortionGrams(
  request: AdminFoodRequestDetail,
  flags: readonly NewFoodFlag[],
  choices: FlagChoices,
): { label: string; grams: number } | null {
  const proposed = request.draft?.proposed?.portions ?? [];
  const fromList = (list: readonly { label: string; grams: number; isDefault: boolean }[]) => {
    const portion = list.find((entry) => entry.isDefault) ?? list[0];
    return portion === undefined ? null : { label: portion.label, grams: portion.grams };
  };
  if (!flags.some((flag) => flag.field === 'portions')) return fromList(proposed);
  const choice = choices.portions;
  if (choice?.option === 'ai') return fromList(proposed);
  if (choice?.option === 'estimate') return fromList(request.draft?.current?.portions ?? []);
  if (choice?.option === 'own') {
    const own = ownPortion(choice.portion);
    return own === null ? null : { label: own.label, grams: own.grams };
  }
  return null;
}

function checkOf(kcal: number, macros: FoodMacrosView): { ok: boolean; text: string } {
  const gap = macroGap(kcal, macros);
  const pct = Math.abs(gap) * 100;
  if (pct < 0.5) return { ok: true, text: 'Matches the macros' };
  if (pct < 1) return { ok: true, text: 'Within 1% of the macros' };
  return { ok: false, text: `${pct.toFixed(1)}% ${gap < 0 ? 'below' : 'above'} the macros` };
}

function FlagBlock({
  flag,
  request,
  choice,
  portionGrams,
  macros,
  sourcesOpen,
  onToggleSources,
  onPick,
}: {
  readonly flag: NewFoodFlag;
  readonly request: AdminFoodRequestDetail;
  readonly choice: FlagChoice | undefined;
  readonly portionGrams: { label: string; grams: number } | null;
  readonly macros: FoodMacrosView | null;
  readonly sourcesOpen: boolean;
  readonly onToggleSources: () => void;
  readonly onPick: (choice: FlagChoice) => void;
}) {
  const done = isDecided(flag, choice);
  const sourcesId = useId();
  const proposedMacros = request.draft?.proposed?.macrosPer100g ?? null;
  const estimateMacros = request.draft?.current?.macrosPer100g ?? null;
  const per = portionGrams;

  const optionBody = (id: OptionId): { title: ReactNode; lines: string[] } => {
    if (flag.field === 'nutrition') {
      const values = id === 'ai' ? proposedMacros : id === 'estimate' ? estimateMacros : null;
      if (values === null) return { title: 'Your figures', lines: ['Type energy and macros per 100 g'] };
      const check = checkOf(values.kcal, values);
      return {
        title: `${figure(values.kcal)} kcal per 100 g`,
        lines: [
          per === null
            ? 'Pick a portion to see its kcal'
            : `${String(Math.round((values.kcal * per.grams) / 100))} kcal a ${per.label}`,
          check.text,
        ],
      };
    }
    if (flag.field === 'portions') {
      const list =
        id === 'ai'
          ? (request.draft?.proposed?.portions ?? [])
          : id === 'estimate'
            ? (request.draft?.current?.portions ?? [])
            : [];
      const portion = list.find((entry) => entry.isDefault) ?? list[0];
      if (portion === undefined) return { title: 'Your portion', lines: ['Name a portion and its grams'] };
      const kcal = macros === null ? null : Math.round((macros.kcal * portion.grams) / 100);
      return {
        title: `${figure(portion.grams)} g`,
        lines: [
          `1 ${portion.label}${list.length > 1 ? `, and ${plural(list.length - 1, 'more portion')}` : ''}`,
          kcal === null ? 'Pick the nutrition to see its kcal' : `${String(kcal)} kcal a ${portion.label}`,
        ],
      };
    }
    const lines = request.draft?.food?.recipeLines ?? [];
    return id === 'ai'
      ? {
          title: plural(lines.length, 'ingredient'),
          lines: [
            lines
              .slice(0, 3)
              .map((line) => line.rawName)
              .join(', '),
          ],
        }
      : { title: 'No recipe for now', lines: [] };
  };

  return (
    <div className="flex flex-col gap-2.5 border-t border-border px-[18px] pt-3.5 pb-3">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
        <h3 className="text-[13px] font-semibold text-foreground">{flag.label}</h3>
        <span
          className={cn(
            'inline-flex h-5 items-center rounded-[10px] px-1.5 text-xs font-medium',
            done ? 'bg-ok-bg text-ok-fg' : 'bg-warn-bg text-warn-fg',
          )}
        >
          {done ? 'Checked' : 'Not checked'}
        </span>
        <span className="text-xs text-muted-foreground">
          AI confidence: {CONFIDENCE_LABELS[flag.confidence]}
        </span>
      </div>
      {flag.note === null ? null : (
        <p className="max-w-[80ch] text-[13px] text-foreground">
          <span className="font-medium text-muted-foreground">AI’s note: </span>
          {flag.note}
        </p>
      )}
      <div role="group" aria-label={flag.label} className="flex flex-wrap items-stretch gap-2">
        {flag.options.map((option, index) => {
          const pressed = choice?.option === option.id;
          const body = optionBody(option.id);
          return (
            <button
              key={option.id}
              type="button"
              aria-pressed={pressed}
              onClick={() => {
                onPick({
                  option: option.id,
                  ...defaultsFor(flag.field, option.id, request),
                  ...(pressed ? choice : {}),
                });
              }}
              className={cn(
                'flex min-w-[188px] flex-col items-start gap-px rounded-lg border px-3 pt-2 pb-[9px] text-left text-[13px] leading-snug transition-colors',
                pressed
                  ? 'border-foreground bg-foreground text-background'
                  : 'border-input bg-card text-foreground hover:bg-accent',
              )}
            >
              <span className="flex w-full items-center gap-2 font-medium">
                {typeof body.title === 'string' ? <Figures text={body.title} /> : body.title}
                <span
                  aria-hidden="true"
                  className={cn(
                    'ml-auto inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-[5px] border px-1 font-mono text-xs font-normal',
                    pressed
                      ? 'border-background/35 text-background/75'
                      : 'border-input text-muted-foreground',
                  )}
                >
                  {index + 1}
                </span>
              </span>
              {body.lines.map((line) => (
                <Figures
                  key={line}
                  text={line}
                  className={cn('text-xs', pressed ? 'text-background/75' : 'text-muted-foreground')}
                />
              ))}
              <span className={cn('text-xs', pressed ? 'text-background/75' : 'text-muted-foreground')}>
                {option.source}
              </span>
            </button>
          );
        })}
      </div>

      {choice?.option === 'own' && flag.field === 'nutrition' ? (
        <OwnNutrition
          value={choice.nutrition ?? EMPTY_NUTRITION}
          onChange={(nutrition) => {
            onPick({ option: 'own', nutrition });
          }}
        />
      ) : null}
      {choice?.option === 'own' && flag.field === 'portions' ? (
        <OwnPortion
          value={choice.portion ?? { label: '', unit: 'piece', grams: '' }}
          onChange={(portion) => {
            onPick({ option: 'own', portion });
          }}
        />
      ) : null}

      {flag.field === 'nutrition' ? <ConsoleCheck macros={macros} /> : null}

      {flag.sources.length === 0 ? (
        <span className="text-xs text-muted-foreground">No source supports this field yet.</span>
      ) : (
        <>
          <button
            type="button"
            aria-expanded={sourcesOpen}
            aria-controls={sourcesId}
            onClick={onToggleSources}
            className="-ml-1.5 inline-flex h-8 items-center gap-1 self-start rounded-md px-1.5 text-[13px] font-medium text-info-fg hover:underline"
          >
            {sourcesOpen ? 'Hide sources' : 'Show sources'} ({flag.sources.length})
            <ChevronDownIcon
              aria-hidden="true"
              className={cn('size-3.5 transition-transform', sourcesOpen && 'rotate-180')}
            />
          </button>
          {sourcesOpen ? (
            <ol id={sourcesId} className="-mx-[18px] -mt-1">
              {flag.sources.map((index) => {
                const source = request.sources[index];
                return source === undefined ? null : <SourceItem key={index} source={source} index={index} />;
              })}
            </ol>
          ) : null}
        </>
      )}
    </div>
  );
}

function ConsoleCheck({ macros }: { readonly macros: FoodMacrosView | null }) {
  const check = macros === null ? null : checkOf(macros.kcal, macros);
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-medium text-muted-foreground">Console check</span>
      <span className="flex items-start gap-[7px] text-[13px]">
        {check === null ? (
          <span
            role="img"
            aria-label="Not chosen yet"
            className="mt-0.5 inline-flex size-4 shrink-0 items-center justify-center rounded-full bg-muted"
          >
            <MinusIcon aria-hidden="true" className="size-[11px] text-muted-foreground" />
          </span>
        ) : check.ok ? (
          <span
            role="img"
            aria-label="Passes"
            className="mt-0.5 inline-flex size-4 shrink-0 items-center justify-center rounded-full bg-ok-bg"
          >
            <CheckIcon aria-hidden="true" className="size-[11px] text-ok-fg" />
          </span>
        ) : (
          <span
            role="img"
            aria-label="Does not match"
            className="mt-0.5 inline-flex size-4 shrink-0 items-center justify-center rounded-full bg-warn-bg"
          >
            <TriangleAlertIcon aria-hidden="true" className="size-[11px] text-warn-fg" />
          </span>
        )}
        {macros === null || check === null ? (
          <span>Not chosen yet. Each option above shows how it compares.</span>
        ) : (
          <span className="flex flex-col gap-px">
            <span>
              Protein, carbohydrate and fat add up to{' '}
              <span className="font-mono tabular-nums">{macroKcal(macros).toFixed(1)}</span> kcal per 100 g:{' '}
              <span className="font-mono text-muted-foreground tabular-nums">
                {macros.proteinG.toFixed(1)} × 4 + {macros.carbsG.toFixed(1)} × 4 + {macros.fatG.toFixed(1)} ×
                9
              </span>
            </span>
            <Figures
              text={`You picked ${figure(macros.kcal)}: ${check.text.charAt(0).toLowerCase()}${check.text.slice(1)}.`}
            />
          </span>
        )}
      </span>
    </div>
  );
}

function OwnNutrition({
  value,
  onChange,
}: {
  readonly value: NonNullable<FlagChoice['nutrition']>;
  readonly onChange: (value: NonNullable<FlagChoice['nutrition']>) => void;
}) {
  const fields: readonly [keyof typeof value, string][] = [
    ['kcal', 'Energy, kcal'],
    ['proteinG', 'Protein, g'],
    ['carbsG', 'Carbohydrate, g'],
    ['fatG', 'Fat, g'],
    ['fibreG', 'Fibre, g (optional)'],
  ];
  const valid = ownMacros(value) !== null;
  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className="pb-1 text-xs font-medium text-muted-foreground">Your figures per 100 g</legend>
      <div className="flex flex-wrap gap-2">
        {fields.map(([key, label]) => (
          <label key={key} className="flex w-[130px] flex-col gap-1 text-xs text-muted-foreground">
            {label}
            <Input
              type="number"
              inputMode="decimal"
              min={0}
              step="any"
              value={value[key]}
              onChange={(event) => {
                onChange({ ...value, [key]: event.target.value });
              }}
              className="h-8 rounded-lg font-mono text-[13px]"
            />
          </label>
        ))}
      </div>
      {valid ? null : (
        <span className="text-xs text-muted-foreground">
          Energy 0 to 905 kcal, and protein, carbohydrate and fat 0 to 100 g, all required.
        </span>
      )}
    </fieldset>
  );
}

function OwnPortion({
  value,
  onChange,
}: {
  readonly value: NonNullable<FlagChoice['portion']>;
  readonly onChange: (value: NonNullable<FlagChoice['portion']>) => void;
}) {
  const unitId = useId();
  const valid = ownPortion(value) !== null;
  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className="pb-1 text-xs font-medium text-muted-foreground">Your default portion</legend>
      <div className="flex flex-wrap gap-2">
        <label className="flex w-[160px] flex-col gap-1 text-xs text-muted-foreground">
          Called
          <Input
            value={value.label}
            onChange={(event) => {
              onChange({ ...value, label: event.target.value });
            }}
            placeholder="slice"
            className="h-8 rounded-lg text-[13px]"
          />
        </label>
        <label htmlFor={unitId} className="flex w-[130px] flex-col gap-1 text-xs text-muted-foreground">
          Unit
          <select
            id={unitId}
            value={value.unit}
            onChange={(event) => {
              onChange({ ...value, unit: event.target.value as PortionUnitName });
            }}
            className="h-8 rounded-lg border border-input bg-card px-2 text-[13px] text-foreground"
          >
            {PORTION_UNITS.map((unit) => (
              <option key={unit} value={unit}>
                {unit}
              </option>
            ))}
          </select>
        </label>
        <label className="flex w-[110px] flex-col gap-1 text-xs text-muted-foreground">
          Grams
          <Input
            type="number"
            inputMode="decimal"
            min={1}
            step="any"
            value={value.grams}
            onChange={(event) => {
              onChange({ ...value, grams: event.target.value });
            }}
            className="h-8 rounded-lg font-mono text-[13px]"
          />
        </label>
      </div>
      {valid ? null : (
        <span className="text-xs text-muted-foreground">
          A name that does not start with a number, and its grams.
        </span>
      )}
    </fieldset>
  );
}

interface DraftRow {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly field: AdminFoodRequestField | null;
  readonly flagged: boolean;
}

interface DraftGroup {
  readonly title: string;
  readonly rows: readonly DraftRow[];
}

function draftGroups(
  request: AdminFoodRequestDetail,
  flags: readonly NewFoodFlag[],
  choices: FlagChoices,
): DraftGroup[] {
  const food = request.draft?.food ?? null;
  const fields = request.draft?.fields ?? [];
  const fieldOf = (name: FoodResearchFieldName) => fields.find((field) => field.field === name) ?? null;
  const flagged = (name: FoodResearchFieldName) => flags.some((flag) => flag.field === name);
  const macros = chosenMacros(request, flags, choices);
  const nutritionValue = (pickValue: (m: FoodMacrosView) => string) =>
    flagged('nutrition') &&
    !flags.some((flag) => flag.field === 'nutrition' && isDecided(flag, choices.nutrition))
      ? 'Not chosen yet'
      : macros === null
        ? 'None'
        : pickValue(macros);
  const portions = request.draft?.proposed?.portions ?? [];
  return [
    {
      title: 'Name and kind',
      rows: [
        { id: 'name', label: 'Name', value: food?.name ?? request.name, field: null, flagged: false },
        {
          id: 'aliases',
          label: 'Other names',
          value: (food?.aliases ?? []).map((alias) => alias.alias).join(', ') || 'None',
          field: null,
          flagged: false,
        },
        { id: 'kind', label: 'Kind', value: 'Dish', field: null, flagged: false },
        {
          id: 'cuisine',
          label: 'Cuisine',
          value: (food?.cuisines ?? []).map((cuisine) => cuisine.replace(/_/g, ' ')).join(', ') || 'None',
          field: null,
          flagged: false,
        },
        {
          id: 'role',
          label: 'Meal role',
          value:
            food?.mealRole === null || food?.mealRole === undefined
              ? 'None'
              : food.mealRole.replace(/_/g, ' '),
          field: null,
          flagged: false,
        },
      ],
    },
    {
      title: 'Diet and allergens',
      rows: [
        {
          id: 'diet',
          label: 'Diet and allergens',
          value:
            choices.recipe?.option === 'ai' || (!flagged('recipe') && (food?.recipeLines.length ?? 0) > 0)
              ? 'From its recipe'
              : `Strictest until a recipe is reviewed${food?.dietClass === null || food?.dietClass === undefined ? '' : `; the AI suggests ${food.dietClass.replace(/_/g, ' ')}`}`,
          field: fieldOf('recipe'),
          flagged: flagged('recipe'),
        },
      ],
    },
    {
      title: 'Per 100 g',
      rows: [
        {
          id: 'kcal',
          label: 'Energy',
          value: nutritionValue((m) => `${figure(m.kcal)} kcal`),
          field: fieldOf('nutrition'),
          flagged: flagged('nutrition'),
        },
        {
          id: 'protein',
          label: 'Protein',
          value: nutritionValue((m) => `${figure(m.proteinG)} g`),
          field: fieldOf('nutrition'),
          flagged: flagged('nutrition'),
        },
        {
          id: 'carbs',
          label: 'Carbohydrate',
          value: nutritionValue((m) => `${figure(m.carbsG)} g`),
          field: fieldOf('nutrition'),
          flagged: flagged('nutrition'),
        },
        {
          id: 'fat',
          label: 'Fat',
          value: nutritionValue((m) => `${figure(m.fatG)} g`),
          field: fieldOf('nutrition'),
          flagged: flagged('nutrition'),
        },
        {
          id: 'fibre',
          label: 'Fibre',
          value: nutritionValue((m) => (m.fibreG === null ? 'Not stated' : `${figure(m.fibreG)} g`)),
          field: fieldOf('nutrition'),
          flagged: flagged('nutrition'),
        },
      ],
    },
    {
      title: 'Portions',
      rows:
        portions.length === 0
          ? [
              {
                id: 'portion',
                label: 'Default portion',
                value: flagged('portions') ? 'Not chosen yet' : 'None',
                field: fieldOf('portions'),
                flagged: flagged('portions'),
              },
            ]
          : portions.map((portion, index) => ({
              id: `portion-${String(index)}`,
              label: `${portion.label.charAt(0).toUpperCase()}${portion.label.slice(1)}${portion.isDefault ? ', default' : ''}`,
              value: `${figure(portion.grams)} g`,
              field: fieldOf('portions'),
              flagged: flagged('portions'),
            })),
    },
  ];
}

function confidenceOfRow(row: DraftRow): ConfidenceLevel {
  return row.field?.confidence ?? 'none';
}

function DraftPanel({
  request,
  flags,
  choices,
  showAll,
  selected,
  onToggle,
  onSelect,
}: {
  readonly request: AdminFoodRequestDetail;
  readonly flags: readonly NewFoodFlag[];
  readonly choices: FlagChoices;
  readonly showAll: boolean;
  readonly selected: string;
  readonly onToggle: () => void;
  readonly onSelect: (id: string) => void;
}) {
  const groups = draftGroups(request, flags, choices);
  const rows = groups.flatMap((group) => group.rows);
  const settled = rows.filter((row) => !row.flagged);
  return (
    <Card
      title="The AI’s draft"
      description={`${plural(rows.length, 'field')}. Click one to see the sources behind it.`}
      className="flex-[13_1_520px]"
      action={
        <Button variant="outline" size="sm" asChild>
          <Link
            to="/foods/new"
            search={{ name: request.draft?.food?.name ?? request.name, requestId: request.id }}
          >
            <PencilIcon aria-hidden="true" className="size-[13px]" />
            Edit fields
          </Link>
        </Button>
      }
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-border bg-secondary/40 px-[18px] py-2.5">
        <span className="flex min-w-0 flex-[1_1_300px] flex-col gap-0.5">
          <span className="font-medium text-foreground">
            {plural(settled.length, 'field')} need no pick from you
          </span>
          <span className="max-w-[70ch] text-xs text-muted-foreground">
            {settled.map((row) => row.label.toLowerCase()).join(', ')}
          </span>
        </span>
        <Button
          variant="outline"
          size="sm"
          aria-expanded={showAll}
          aria-controls="draft-rows"
          onClick={onToggle}
        >
          {showAll ? 'Hide them' : 'Show them'}
          <ChevronDownIcon
            aria-hidden="true"
            className={cn('size-3.5 transition-transform', showAll && 'rotate-180')}
          />
        </Button>
      </div>
      <div id="draft-rows">
        {showAll ? (
          <>
            <div className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)_96px_108px] gap-3 border-t border-border bg-secondary px-[18px] py-2 text-[11px] font-semibold tracking-[0.055em] text-muted-foreground uppercase">
              <span>Field</span>
              <span>Value</span>
              <span>Confidence</span>
              <span>Sources</span>
            </div>
            {groups.map((group) => (
              <div key={group.title}>
                <div className="border-t border-border px-[18px] pt-2.5 pb-1.5 text-xs font-semibold text-muted-foreground">
                  {group.title}
                </div>
                {group.rows.map((row) => (
                  <button
                    key={row.id}
                    type="button"
                    aria-pressed={selected === row.id}
                    onClick={() => {
                      onSelect(row.id);
                    }}
                    className={cn(
                      'grid min-h-[38px] w-full grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)_96px_108px] items-center gap-3 border-t border-border px-[18px] py-2 text-left text-[13px]',
                      selected === row.id ? 'bg-secondary' : 'bg-card hover:bg-accent',
                    )}
                  >
                    <span className={cn('text-muted-foreground', selected === row.id && 'font-semibold')}>
                      {row.label}
                    </span>
                    <span className="flex flex-wrap items-center gap-1.5 font-medium text-foreground">
                      <Figures text={row.value} />
                      {row.flagged && row.value === 'Not chosen yet' ? (
                        <span className="inline-flex h-5 items-center rounded-[10px] bg-warn-bg px-1.5 text-xs font-medium text-warn-fg">
                          Check above
                        </span>
                      ) : null}
                    </span>
                    <span className="text-xs">
                      {row.field === null ? (
                        <span className="text-muted-foreground">{CONFIDENCE_LABELS.none}</span>
                      ) : (
                        <ConfidenceDot level={confidenceOfRow(row)} className="font-normal" />
                      )}
                    </span>
                    <span className="font-mono text-xs text-info-fg">
                      {row.field === null || row.field.sources.length === 0 ? (
                        <span className="font-sans text-muted-foreground">None cited</span>
                      ) : (
                        row.field.sources.map((index) => `[${String(index + 1)}]`).join(' ')
                      )}
                    </span>
                  </button>
                ))}
              </div>
            ))}
          </>
        ) : null}
      </div>
    </Card>
  );
}

function EvidencePanel({
  request,
  flags,
  choices,
  selected,
}: {
  readonly request: AdminFoodRequestDetail;
  readonly flags: readonly NewFoodFlag[];
  readonly choices: FlagChoices;
  readonly selected: string;
}) {
  const row = draftGroups(request, flags, choices)
    .flatMap((group) => group.rows)
    .find((entry) => entry.id === selected);
  if (row === undefined) return null;
  const note =
    row.field?.note ??
    (row.field === null
      ? 'The research filled this in from what it read, without grading it or citing a source for it.'
      : 'The research left no note on this field.');
  return (
    <Card title={`${row.label}: ${row.value}`} eyebrow="Evidence for">
      <div className="flex flex-col gap-0.5 border-t border-border px-[18px] py-3">
        <span className="text-xs font-medium text-muted-foreground">AI’s note</span>
        <p className="max-w-[70ch] text-[13px] text-foreground">{note}</p>
      </div>
      {row.field === null || row.field.sources.length === 0 ? (
        <p className="border-t border-border px-[18px] py-2.5 text-xs text-muted-foreground">
          No source is cited for it.
        </p>
      ) : (
        <ol>
          {row.field.sources.map((index) => {
            const source = request.sources[index];
            return source === undefined ? null : <SourceItem key={index} source={source} index={index} />;
          })}
        </ol>
      )}
    </Card>
  );
}

function LogsPanel({
  request,
  macros,
  range,
}: {
  readonly request: AdminFoodRequestDetail;
  readonly macros: FoodMacrosView | null;
  readonly range: readonly number[];
}) {
  const logs = request.estimateLogs;
  const after = (grams: number): string => {
    if (macros !== null) return String(Math.round((macros.kcal * grams) / 100));
    if (range.length === 0) return 'Not chosen';
    const values = range.map((kcal) => Math.round((kcal * grams) / 100));
    const low = Math.min(...values);
    const high = Math.max(...values);
    return low === high ? String(low) : `${String(low)} to ${String(high)}`;
  };
  if (logs === null) {
    return (
      <Card title="Meals logged on the estimate" className="flex-[1_1_460px]">
        <p className="border-t border-border px-[18px] py-3 text-[13px] text-muted-foreground">
          It could not be estimated, so no meal was logged on it.
        </p>
      </Card>
    );
  }
  const latest = logs.recent[0];
  const name = request.food?.name ?? request.name;
  return (
    <Card title="Meals logged on the estimate" className="flex-[1_1_460px]">
      <p className="-mt-2 px-[18px] pb-3 text-xs text-muted-foreground">
        {plural(logs.count, 'log')} by {plural(logs.people, 'person', 'people')} keep their food and their
        grams. When you create the food they show its numbers and lose the estimated label.
      </p>
      {latest === undefined ? null : (
        <>
          <CardRow
            label="Their card now"
            name={name}
            amount={amountOf(latest)}
            kcal={String(Math.round(latest.kcalNow))}
            estimated
          />
          <CardRow
            label="After you create it"
            name={name}
            amount={amountOf(latest)}
            kcal={after(latest.grams)}
          />
        </>
      )}
      <div className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_48px_100px] gap-3 border-t border-border bg-secondary px-[18px] py-2 text-[11px] font-semibold tracking-[0.055em] text-muted-foreground uppercase">
        <span>Logged</span>
        <span>Amount</span>
        <span className="text-right">Now</span>
        <span className="text-right">After</span>
      </div>
      {logs.recent.slice(0, 5).map((log) => (
        <div
          key={log.itemId}
          className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_48px_100px] items-center gap-3 border-t border-border px-[18px] py-2 text-[13px]"
        >
          <span>
            {log.localDate}, {MEAL_TYPES[log.mealType] ?? log.mealType}
          </span>
          <Figures text={amountOf(log)} className="text-muted-foreground" />
          <span className="text-right font-mono text-muted-foreground tabular-nums">
            {Math.round(log.kcalNow)}
          </span>
          <Figures text={after(log.grams)} className="text-right" />
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-border bg-secondary/40 px-[18px] py-2.5 text-xs text-muted-foreground">
        <span>{logs.count > 5 ? plural(logs.count - 5, 'more log') : 'Every log is shown'}</span>
        <span className="ml-auto text-[13px] text-foreground">
          <Figures
            text={`All ${plural(logs.count, 'log')} (${figure(logs.grams)} g): ${String(Math.round(logs.kcalNow))} kcal now, ${after(logs.grams)} after`}
          />
        </span>
      </div>
    </Card>
  );
}

function amountOf(log: { portionLabel: string | null; grams: number }): string {
  return log.portionLabel === null ? `${figure(log.grams)} g` : `${log.portionLabel}, ${figure(log.grams)} g`;
}

function CardRow({
  label,
  name,
  amount,
  kcal,
  estimated = false,
}: {
  readonly label: string;
  readonly name: string;
  readonly amount: string;
  readonly kcal: string;
  readonly estimated?: boolean;
}) {
  return (
    <div className="grid grid-cols-[132px_minmax(0,1fr)_112px] items-center gap-3 border-t border-border px-[18px] py-2.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-medium text-foreground">{name}</span>
        <Figures text={amount} className="text-xs text-muted-foreground" />
        {estimated ? (
          <span className="inline-flex h-5 items-center rounded-[10px] bg-muted px-1.5 text-xs font-medium text-muted-foreground">
            Estimated by AI
          </span>
        ) : null}
      </span>
      <span className={cn('text-right text-[13px]', !estimated && 'font-medium')}>
        <Figures text={kcal} /> kcal
      </span>
    </div>
  );
}

function KeepEstimate({
  kcal,
  researched,
  canMutate,
  isPending,
  error,
  onKeep,
}: {
  readonly kcal: number;
  readonly researched: number | null;
  readonly canMutate: boolean;
  readonly isPending: boolean;
  readonly error: string | null;
  readonly onKeep: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  const id = useId();
  const whyId = useId();
  const gap = researched === null || researched === 0 ? null : (kcal - researched) / researched;
  const why = !canMutate ? ADMIN_ONLY : reason.trim().length < 3 ? 'Say why it is right first.' : null;
  return (
    <div className="flex flex-col gap-2 border-r border-border px-[18px] pt-3.5 pb-4">
      <h3 className="text-[13px] font-semibold text-foreground">
        Keep the estimate (<span className="font-mono font-medium tabular-nums">{figure(kcal)}</span> kcal)
      </h3>
      <p className="max-w-[70ch] text-xs text-muted-foreground">
        Keeps {figure(kcal)} kcal per 100 g and marks it reviewed.
        {gap === null || Math.abs(gap) < 0.1
          ? ' Say why it is right.'
          : ` It is ${String(Math.round(Math.abs(gap) * 100))}% ${gap > 0 ? 'above' : 'below'} the researched figure, so say why it is right.`}
      </p>
      <label htmlFor={id} className="text-xs font-medium text-foreground">
        Why {figure(kcal)} is right <span className="font-normal text-muted-foreground">(required)</span>
      </label>
      <Textarea
        id={id}
        rows={2}
        maxLength={500}
        value={reason}
        onChange={(event) => {
          setReason(event.target.value);
        }}
        placeholder="The Indian bakery version uses more butter; label attached."
        className="text-[13px]"
      />
      {error === null ? null : (
        <p role="alert" className="text-[12.5px] font-medium text-err-fg">
          {error}
        </p>
      )}
      <span className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          aria-disabled={why !== null || isPending}
          aria-describedby={why === null ? undefined : whyId}
          className={cn((why !== null || isPending) && 'cursor-not-allowed opacity-60')}
          onClick={() => {
            if (why === null && !isPending) onKeep(reason);
          }}
        >
          {isPending ? 'Keeping…' : 'Keep the estimate'}
        </Button>
        {why === null ? null : (
          <span id={whyId} className="text-xs text-muted-foreground">
            {why}
          </span>
        )}
      </span>
    </div>
  );
}
